import { useState, useMemo } from 'react'
import { Input } from '@neo/primitives/input'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { useParams } from 'router-v6'
import { queryClient } from 'v2/configs/rq-setup'
import type { Channels, LiveAgent } from 'v2/types/api/conversations'
import { SuspenseLoader } from 'v2/shared/components/suspense-loader'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import { EmptyState } from 'v2/shared/components/empty-state'
import { useLiveAgentsQuery } from '../../queries'
import { useTransferMutations } from '../../mutations/conversations'
import { AgentItem } from './agent-item'
import type { TransferSuccessProps } from './types'
import { getConversationForTransfer } from './utils'

interface AgentListProps extends TransferSuccessProps {
  channel: Channels
}

export const AgentList = ({ channel, onTransferSuccess }: AgentListProps) => {
  const { data: agents, isLoading: agentsLoading, error } = useLiveAgentsQuery(channel)
  const [searchTerm, setSearchTerm] = useState('')
  const { conversationId } = useParams()
  const { transfer } = useTransferMutations()

  const filteredAgents = useMemo(() => {
    if (!agents) return []
    return agents.filter((agent) => {
      const fullName = `${agent.account_first_name} ${agent.account_last_name}`.toLowerCase()
      const email = agent.email.toLowerCase()
      const search = searchTerm.toLowerCase()
      return agent.status_availability && (fullName.includes(search) || email.includes(search))
    })
  }, [agents, searchTerm])

  const handleTransfer = (agent: LiveAgent, type: 'warm' | 'cold') => {
    const conversation = getConversationForTransfer(conversationId, channel)
    if (!conversation.child?.channel || !conversation.parent?.contact_uuid) return

    transfer.mutate(
      {
        channel: conversation.child?.channel,
        transferType: type,
        contactUuid: conversation.parent?.contact_uuid,
        identifier: agent.id,
        agentOrTeam: 'agent',
      },
      {
        onSuccess: () => {
          onTransferSuccess({
            name: `${agent.account_first_name} ${agent.account_last_name}`,
            identifier: agent.id,
            participantType: 'agent',
            type,
          })
        },
      },
    )
  }

  if (agentsLoading)
    return (
      <div className="relative h-[352px]">
        <SuspenseLoader text="Fetching available agents..." />
      </div>
    )

  if (error)
    return (
      <EmptyState
        type="app_error"
        text="Error fetching agents"
        actions={[
          {
            text: 'Retry',
            onClick: () => {
              queryClient.resetQueries({
                queryKey: conversationQueryKeys().liveAgents(channel),
              })
            },
          },
        ]}
      />
    )

  return (
    <div>
      <div className="px-1">
        <Input
          type="text"
          placeholder="Search agents"
          className="w-full mb-2"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="input-agent-search"
        />
      </div>
      <ScrollArea className="flex max-h-[300px] flex-col overflow-y-auto -mr-4">
        <div className="pr-4">
          {filteredAgents.map((agent) => (
            <AgentItem
              key={agent.id}
              agent={agent}
              onTransfer={(type) => handleTransfer(agent, type)}
              isDisabled={transfer.isPending}
            />
          ))}
          {filteredAgents.length === 0 && (
            <div className="text-s-400 text-tertiary truncate">No agents found</div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
